import type { AsnFormat } from '@ipfabric/utils/formatter';
import type { Timezone } from '../../timezones';
import type { TableFilter } from '../filters/TableFilter';
import type { TableColumnConfig } from './TableColumnConfig';

export interface TableValueFormattingContext {
  timezone: Timezone;
  asnFormat: AsnFormat;
}

/**
 * Definition of a column in table
 *
 * Pitfalls encountered:
 * - Mapping of columns to fields in data is not 1:1 even in export.
 *   That's why the column contains high level of abstraction how it accesses data.
 *   Required fields to show the column should be fetched in dependencies (including the basic one)
 *   and column factory functions should guard that this is properly set up.
 * - Formatting human-readable value is sometimes dependent on outer conditions - in our case
 *   datetime fields are formatted according to timezone settings, other locale stuff may appear in the future.
 *   That's why we use global TableValueFormattingContext, hopefully it will be enough to cover
 *   all similar cases in the future.
 * - TODO reverse-parsing value from human string: this will be used for frontend filters and let's design it
 *   when that time comes. Since the column is not always bound to a particular data field, it may require
 *   some well-thought abstraction.
 */
export type TableColumnBase<TRow, TFormat> = TableColumnConfig & {
  /**
   * Contains all intel needed to render given column in any known context
   *
   * - format type (text, number, date, whatever)
   * - format type settings (decimal numbers etc.)
   * - columns needed to build the value (ids for links etc.)
   */
  format: TFormat;
  /**
   * List of fields required to produce a value by toCsvString
   *
   * Some fields may introduce additional JOIN operations, so we should
   * avoid fetching all table fields from database even for export on backend.
   */
  csvStringDependencies: (keyof TRow)[];
  /**
   * Retrieve human-string value from table row
   *
   * Assumed usage:
   * - CSV exports
   * - base for rendering the value on frontend (may be additionally
   *   colored, wrapped with links etc)
   */
  toCsvString: (row: TRow, context: TableValueFormattingContext) => string;
  filter?: TableFilter;
  /**
   * List of fields required to render the column on frontend
   *
   * For example, identifiers or filter values for creating hyperlinks.
   */
  webDependencies: (keyof TRow)[];
};
